/*
 * PEARSON PROPRIETARY AND CONFIDENTIAL INFORMATION SUBJECT TO NDA
 * Copyright © 2025 Pearson Education, Inc.
 * All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Pearson Education, Inc.  The intellectual and technical concepts contained
 * herein are proprietary to Pearson Education, Inc. and may be covered by U.S. and Foreign Patents,
 * patent applications, and are protected by trade secret or copyright law.
 * Dissemination of this information, reproduction of this material, and copying or distribution of this software
 * is strictly forbidden unless prior written permission is obtained
 * from Pearson Education, Inc.
 */

/* eslint-disable no-param-reassign */
import { types, applySnapshot } from 'mobx-state-tree';
import Framework from '@greenville/framework';

import * as constants from '../../constants/ServiceConstants';

// Individual graph item model
const GraphItem = types.model('GraphItem', {
  filename: types.maybeNull(types.string),
  name: types.maybeNull(types.string),
  lastModified: types.maybeNull(types.string),
  isActive: types.optional(types.boolean, false),
  graphId: types.maybeNull(types.string),
  version: types.maybeNull(types.number),
  lifeCycleStatus: types.maybeNull(types.string),
  isDefault: types.optional(types.boolean, false),
  defaultVersion: types.maybeNull(types.union(types.number, types.null)),
  modifiedBy: types.maybeNull(types.string)
});

// Main GraphsModel
const GraphsModel = types
  .model('GraphsModel', {
    graphs: types.optional(types.array(GraphItem), []),
    success: types.optional(types.boolean, false)
  })
  .views((self) => ({
    get graphsList() {
      return self.graphs;
    },
    get hasData() {
      return self.graphs.length > 0;
    },
    getGraphById(graphId) {
      return self.graphs.find((item) => item.graphId === graphId);
    },
    getGraphsByName(name) {
      return self.graphs.filter((item) => item.name === name);
    },
    get activeGraphs() {
      return self.graphs.filter((item) => item.isActive === true);
    }
  }))
  .actions((self) => ({
    fetchGraphs(payload = {}) {
      Framework.getEventManager().publish(constants.GET_GRAPHS_LIST, {
        payload
      });
    },
    setResponse(response) {
      if (!response) {
        Framework.getEventManager().publish(constants.GET_GRAPHS_LIST_RESPONSE);
        return;
      }

      let responseData = {
        graphs: [],
        success: false
      };

      if (response.success !== undefined) {
        responseData = {
          graphs: response.graphs || [],
          success: response.success || false
        };
      } else if (response.graphs && Array.isArray(response.graphs)) {
        responseData = {
          graphs: response.graphs,
          success: true
        };
      } else if (Array.isArray(response)) {
        responseData = {
          graphs: response,
          success: true
        };
      }

      // Apply snapshot for graphs array
      applySnapshot(self.graphs, responseData.graphs);
      self.success = responseData.success;

      Framework.getEventManager().publish(constants.GET_GRAPHS_LIST_RESPONSE);
    },
    resetData() {
      applySnapshot(self.graphs, []);
      self.success = false;
    },
    resetStoreValues() {
      self.resetData();
      Framework.getEventManager().publish(constants.GET_GRAPHS_LIST_RESPONSE);
    }
  }));

export default GraphsModel;
